import React from 'react';
import { IProject } from 'store/companies/companiesTypes';
import { List } from './styles';
import { display } from 'components/ErrorView/ErrorDialog';
import TaskListView from 'components/TaskListView';
import TaskListNew from 'components/TaskListNew';
import { IRoleOption, IUserOption } from 'components/UserSelector/UserSelector';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { mergeRefs } from 'utils/happyFunctions';
import * as taskManipulate from 'store/tasks/taskManipulate';
import { ITasks } from 'store/store';
import { ITask } from 'store/tasks/tasksType';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { WindowSize } from '../../../components/TaskListView/TaskListView';

interface IComponentProps {
  tasks: ITasks['data'];
  parent: ITask;
  thisUser: IThisUserState;
  companyId?: number;
  project?: IProject;
  onUpdateTask: (task: ITask) => void;
  requireTasks: () => Promise<any>;
  showProgress: boolean;
  showWeight: boolean;
  allowCreate: boolean;
  availableTemplates: ITasks;
  filteredTasks: ITasks['data'];
  allSubtasks: ITasks['data'];
  onClickOnTask: any;
  companyOwnerId: number;
  selectedSubTaskId?: number;
  allTasksFetched: boolean;
  onUpdateAssignment: any;
  selectedTemplate: any | undefined;
  canEditSubTasks?: boolean;
  windowSize: WindowSize;
  members: { [key: number]: IUserOption | IRoleOption };
  updateTask: (task: ITask) => Promise<any>;
  isDraft?: boolean;
}

interface IComponentStates {
  loading: boolean;
  requestSent: boolean;
  loadMoreButton: boolean;
  tempOrderOverride: { [key: number]: number };
}

class SubtaskList extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  state = {
    requestSent: false,
    loading: true,
    loadMoreButton: false,
    tempOrderOverride: {},
  };
  taskList: React.RefObject<HTMLUListElement> = React.createRef();
  handleDragEnd = (result: DropResult) => {
    const { filteredTasks, updateTask } = this.props;
    const tempOrderOverride = this.state;
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    const newIndex = result.destination.index;
    const oldIndex = result.source.index;
    // when order not changed do nothing
    if (newIndex === oldIndex) {
      return;
    }
    let newOrder;

    const sortedSubTasks = Object.values(filteredTasks).sort(
      (a, b) => this.getOrder(a) - this.getOrder(b)
    );
    const item = sortedSubTasks[oldIndex];

    if (newIndex === 0) {
      // dropped in first of list
      if (sortedSubTasks.length === 0) {
        // dropped in empty list
        newOrder = 0;
      } else {
        const bottomItemOrder = this.getOrder(sortedSubTasks[newIndex]);
        newOrder = bottomItemOrder - 1;
      }
    } else if (newIndex + 1 === sortedSubTasks.length) {
      // dropped in end of list
      const topItemOrder = this.getOrder(sortedSubTasks[newIndex - 1]);
      newOrder = topItemOrder + 1;
    } else if (newIndex > oldIndex) {
      // pull down a list itemSortedQuestion
      const topItemOrder = this.getOrder(sortedSubTasks[newIndex]);
      const bottomItemOrder = this.getOrder(sortedSubTasks[newIndex + 1]);
      newOrder = (topItemOrder + bottomItemOrder) / 2;
    } else {
      // pull up a list item
      const topItemOrder = this.getOrder(sortedSubTasks[newIndex - 1]);
      const bottomItemOrder = this.getOrder(sortedSubTasks[newIndex]);
      newOrder = (topItemOrder + bottomItemOrder) / 2;
    }
    const task: ITask = taskManipulate.storeToServer(item);
    task.order = newOrder;
    this.setState({
      tempOrderOverride: { ...tempOrderOverride, [task.id]: newOrder },
    });

    const clearOrder = () => {
      if (tempOrderOverride[task.id] === task.order)
        this.setState({
          tempOrderOverride: { ...tempOrderOverride, [task.id]: null },
        });
    };
    // update Task
    updateTask(task).then(
      () => {
        clearOrder();
      },
      () => {
        clearOrder();
      }
    );
  };

  componentDidMount() {
    if (this.taskList.current) {
      this.taskList.current.addEventListener('scroll', this.handleOnScroll);
    }
  }

  componentWillUnmount() {
    if (this.taskList.current) {
      this.taskList.current.removeEventListener('scroll', this.handleOnScroll);
    }
  }

  handleOnScroll = () => {
    if (this.taskList.current) {
      const boundary = 10;
      const scrollTop =
        this.taskList.current.scrollTop || document.body.scrollTop;
      const scrollHeight =
        this.taskList.current.scrollHeight || document.body.scrollHeight;
      const clientHeight =
        this.taskList.current.clientHeight || window.innerHeight;
      const scrolledToBottom =
        Math.ceil(scrollTop + clientHeight) >= scrollHeight - boundary;
      if (scrolledToBottom) {
        if (!this.props.allTasksFetched) {
          if (!this.state.requestSent) {
            this.loadData();
          }
        }
      }
    }
  };

  loadData = () => {
    this.setState({ requestSent: true, loading: true });
    this.props.requireTasks().then(
      () => {
        this.setState({
          loading: false,
          requestSent: false,
          loadMoreButton: false,
        });
      },
      (err: any) => {
        display(err);
        this.setState({ loadMoreButton: true, loading: false });
      }
    );
  };

  render() {
    const {
      tasks,
      thisUser,
      companyId,
      project,
      showProgress,
      showWeight,
      onUpdateTask,
      filteredTasks,
      onClickOnTask,
      companyOwnerId,
      onUpdateAssignment,
      allowCreate,
      members,
      availableTemplates,
      parent,
      allSubtasks,
      selectedTemplate,
      canEditSubTasks,
      windowSize,
    } = this.props;
    return (
      // @ts-expect-error JSX error
      <DragDropContext onDragEnd={this.handleDragEnd}>
        <Droppable droppableId="droppable-1">
          {(provided) => (
            <List
              allowAdd={true}
              ref={mergeRefs(this.taskList, provided.innerRef)}
              {...provided.droppableProps}
            >
              {Object.values(filteredTasks)
                .sort((a, b) => this.grater(a, b))
                .map((item: ITask, key?: number) => (
                  <TaskListView
                    key={item.id}
                    task={item}
                    template={tasks[item.templateId]}
                    thisUser={thisUser}
                    companyId={companyId}
                    projectId={project?.id}
                    thisUserId={Number(thisUser.userId)}
                    onUpdateTask={onUpdateTask}
                    showProgress={showProgress}
                    onClickOnTask={onClickOnTask}
                    companyOwnerId={companyOwnerId}
                    onUpdateAssignment={onUpdateAssignment}
                    showWeight={showWeight}
                    index={key || 0}
                    editable={canEditSubTasks}
                    active={item.id === this.props.selectedSubTaskId}
                    templateSelected={
                      Object.keys(availableTemplates).length === 1 ||
                      selectedTemplate !== undefined
                    }
                    windowSize={windowSize}
                  />
                ))}
              {provided.placeholder}
              {allowCreate && companyId && (
                <TaskListNew
                  parent={parent}
                  templates={availableTemplates}
                  key="new"
                  thisUser={thisUser}
                  companyId={companyId}
                  project={project!}
                  thisUserId={Number(thisUser.userId)}
                  onSaveTask={onUpdateTask}
                  companyOwnerId={companyOwnerId}
                  showWeight={showWeight}
                  members={members}
                  siblings={allSubtasks}
                  windowSize={windowSize}
                />
              )}
            </List>
          )}
        </Droppable>
      </DragDropContext>
    );
  }
  getOrder = (a: ITask) => {
    const { tempOrderOverride } = this.state;

    return tempOrderOverride[a.id] || a.order;
  };

  grater = (a: ITask, b: ITask) => {
    const aOrder = this.getOrder(a);
    const bOrder = this.getOrder(b);
    return aOrder - bOrder;
  };
}

export default SubtaskList;
